import * as React from "react";
import * as TooltipPrimitive from "@radix-ui/react-tooltip";

import { cn } from "../lib/utils";

const TOOLTIP_DELAY_MS = 300;
const TOOLTIP_SKIP_DELAY_MS = 300;

const RemiTooltipProviderContext = React.createContext(false);

const RemiTooltipProvider = ({ children, ...props }: React.ComponentProps<typeof TooltipPrimitive.Provider>) => (
  <RemiTooltipProviderContext.Provider value={true}>
    <TooltipPrimitive.Provider delayDuration={TOOLTIP_DELAY_MS} skipDelayDuration={TOOLTIP_SKIP_DELAY_MS} {...props}>
      {children}
    </TooltipPrimitive.Provider>
  </RemiTooltipProviderContext.Provider>
);

// skipDelayDuration only works across tooltips that share one Provider, so the app-level
// provider is preferred; the per-tooltip fallback exists because Radix throws for a Root
// rendered outside any Provider (tests, isolated renders).
const RemiTooltip = (props: React.ComponentProps<typeof TooltipPrimitive.Root>) => {
  const hasProvider = React.useContext(RemiTooltipProviderContext);
  const root = <TooltipPrimitive.Root {...props} />;
  return hasProvider ? root : <RemiTooltipProvider>{root}</RemiTooltipProvider>;
};

type RemiTooltipTriggerProps = React.ComponentProps<typeof TooltipPrimitive.Trigger> & { disabled?: boolean };

const RemiTooltipTrigger = ({ disabled = false, ...props }: RemiTooltipTriggerProps) => {
  if (disabled) {
    return <>{props.children}</>;
  }

  return <TooltipPrimitive.Trigger {...props} />;
};

type RemiTooltipContentProps = React.ComponentPropsWithoutRef<typeof TooltipPrimitive.Content> & {
  portal?: boolean;
  container?: HTMLElement | null;
};

const contentStyles =
  "text-text-primary animate-in fade-in-0 zoom-in-95 data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=closed]:zoom-out-95 data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2 origin-(--radix-tooltip-content-transform-origin) z-transient overflow-hidden rounded-md border border-border-primary bg-background-primary px-3 py-2 text-[13px] shadow-md max-w-[264px]";

const RemiTooltipContent = ({
  ref,
  className,
  sideOffset = 6,
  portal = true,
  container,
  ...props
}: RemiTooltipContentProps & { ref?: React.Ref<React.ComponentRef<typeof TooltipPrimitive.Content>> }) => {
  if (portal) {
    return (
      <TooltipPrimitive.Portal container={container ?? undefined}>
        <TooltipPrimitive.Content
          ref={ref}
          sideOffset={sideOffset}
          className={cn(contentStyles, className)}
          {...props}
        />
      </TooltipPrimitive.Portal>
    );
  }

  return (
    <TooltipPrimitive.Content ref={ref} sideOffset={sideOffset} className={cn(contentStyles, className)} {...props} />
  );
};
RemiTooltipContent.displayName = "RemiTooltipContent";

export { RemiTooltip, RemiTooltipContent, RemiTooltipProvider, RemiTooltipTrigger };
